import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// Client for /rest/mongo-explorer — powers the MongoDB V1 and MongoDB
// V2 panes under /admin/common-data. All calls admin-gated at the
// backend. Row shape stays open (`any`) so the SPA's dynamic column
// derivation reflects each collection's true fields.
export type MongoStack = 'v1' | 'v2';

export interface MongoDatabase { name: string; sizeOnDisk?: number; empty?: boolean; }

@Injectable({ providedIn: 'root' })
export class MongoExplorerService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {}

  private _opts() { return { withCredentials: true }; }
  private _base() { return `${env.apiUrl}/rest/mongo-explorer`; }

  async listDatabases(stack: MongoStack): Promise<MongoDatabase[]> {
    const id = this.loadingService.add(`Loading databases (${stack})`);
    const url = `${this._base()}/databases?stack=${encodeURIComponent(stack)}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.databases) ? res.databases as MongoDatabase[] : [];
    } catch (e: any) {
      this._toastErr(`Databases (${stack})`, e, 'F2A_MONGO_EXPLORER_DBS_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  async listCollections(stack: MongoStack, db: string): Promise<string[]> {
    const id = this.loadingService.add(`Loading collections (${stack}:${db})`);
    const url = `${this._base()}/collections?stack=${encodeURIComponent(stack)}&db=${encodeURIComponent(db)}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.collections) ? res.collections as string[] : [];
    } catch (e: any) {
      this._toastErr(`Collections (${stack}:${db})`, e, 'F2A_MONGO_EXPLORER_COLS_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  async listRows(stack: MongoStack, db: string, col: string, limit = 5000): Promise<any[]> {
    const id = this.loadingService.add(`Loading rows (${stack}:${db}.${col})`);
    const url = `${this._base()}/rows?stack=${encodeURIComponent(stack)}&db=${encodeURIComponent(db)}&col=${encodeURIComponent(col)}&limit=${limit}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.rows) ? res.rows : [];
    } catch (e: any) {
      this._toastErr(`Rows (${stack}:${db}.${col})`, e, 'F2A_MONGO_EXPLORER_ROWS_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  /**
   * Paged row fetch backing the AG-Grid infinite row model (IT-F2-95).
   * Serializes the filter + sort models as base64 JSON so a complex
   * filterModel doesn't blow the URL length limit. The backend caps
   * `limit` at 5000 per page (SPA typically asks for 100).
   *
   * `count=true` on the first page pull triggers a `countDocuments`
   * on the backend so the grid knows the total row count and can
   * render the correct scrollbar height / status-bar count.
   */
  async listRowsPage(
    stack: MongoStack,
    db: string,
    col: string,
    opts: {
      skip: number;
      limit: number;
      filterModel?: any;
      sortModel?: any;
      count?: boolean;
    },
  ): Promise<{ rows: any[]; total?: number } | null> {
    const parts = [
      `stack=${encodeURIComponent(stack)}`,
      `db=${encodeURIComponent(db)}`,
      `col=${encodeURIComponent(col)}`,
      `skip=${opts.skip}`,
      `limit=${opts.limit}`,
    ];
    if (opts.filterModel && Object.keys(opts.filterModel).length > 0) {
      parts.push(`filter=${encodeURIComponent(this._b64Json(opts.filterModel))}`);
    }
    if (opts.sortModel && opts.sortModel.length > 0) {
      parts.push(`sort=${encodeURIComponent(this._b64Json(opts.sortModel))}`);
    }
    if (opts.count) parts.push('count=1');
    const url = `${this._base()}/rows?${parts.join('&')}`;
    // No loading-service spinner here — the grid pulls many pages
    // as the operator scrolls; a spinner on every fetch would strobe.
    // Grid has its own overlay for the initial pull.
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return {
        rows: Array.isArray(res?.rows) ? res.rows : [],
        total: typeof res?.total === 'number' ? res.total : undefined,
      };
    } catch (e: any) {
      this._toastErr(`Rows (${stack}:${db}.${col})`, e, 'F2A_MONGO_EXPLORER_ROWS_FAIL', url);
      return null;
    }
  }

  private _b64Json(o: any): string {
    try { return btoa(unescape(encodeURIComponent(JSON.stringify(o)))); }
    catch { return btoa(JSON.stringify(o)); }
  }

  async updateRow(stack: MongoStack, db: string, col: string, id: string, patch: Record<string, any>): Promise<any | null> {
    const spin = this.loadingService.add(`Saving row (${stack}:${db}.${col})`);
    const url = `${this._base()}/rows/${encodeURIComponent(id)}?stack=${encodeURIComponent(stack)}&db=${encodeURIComponent(db)}&col=${encodeURIComponent(col)}`;
    try {
      const res: any = await this.http.put(url, { patch }, this._opts()).toPromise();
      return res?.row || null;
    } catch (e: any) {
      this._toastErr(`Save row (${stack}:${db}.${col})`, e, 'F2A_MONGO_EXPLORER_UPDATE_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(spin);
    }
  }

  private _toastErr(title: string, e: any, code: string, requestUrl?: string) {
    const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
    this.toast.error(title, msg, {
      error_code: code,
      customer: 'f2-admin',
      scanner_path: '/admin/common-data',
      request_url: requestUrl || e?.url || undefined,
      http_status: e?.status ?? 0,
      content_type: (e?.headers?.get?.('content-type') as string | undefined) || (e?.error && typeof e.error === 'string' ? 'text/html' : 'application/json'),
      f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
      f2_response_message: msg,
    });
  }
}
